import api from "./axios";
import { API_URL } from "../config/env";

const base = (mintSpaceId) => `/mint-space/${mintSpaceId}/courses`;

export const listCourses = async (mintSpaceId, instanceId = null) => {
  const response = await api.get(base(mintSpaceId), {
    params: instanceId ? { instanceId } : {},
  });
  return response.data;
};

export const createCourse = async (mintSpaceId, payload) => {
  const response = await api.post(base(mintSpaceId), payload);
  return response.data;
};

export const getCourseDetail = async (mintSpaceId, courseId) => {
  const response = await api.get(`${base(mintSpaceId)}/${courseId}`);
  return response.data;
};

export const updateCourse = async (mintSpaceId, courseId, payload) => {
  const response = await api.patch(`${base(mintSpaceId)}/${courseId}`, payload);
  return response.data;
};

export const deleteCourse = async (mintSpaceId, courseId) => {
  const response = await api.delete(`${base(mintSpaceId)}/${courseId}`);
  return response.data;
};

export const reorderCourse = async (mintSpaceId, courseId, chapters) => {
  const response = await api.put(`${base(mintSpaceId)}/${courseId}/reorder`, { chapters });
  return response.data;
};

export const uploadCourseCover = async (mintSpaceId, courseId, file) => {
  const formData = new FormData();
  formData.append("cover", file);
  if (file.name) formData.append("fileName", file.name);
  const response = await api.post(`${base(mintSpaceId)}/${courseId}/cover`, formData, {
    timeout: 0,
  });
  return response.data;
};

export const getCourseCoverUrl = (mintSpaceId, courseId) => {
  const apiBase = API_URL.replace(/\/$/, "");
  const token = localStorage.getItem("token");
  const qs = token ? `?access_token=${encodeURIComponent(token)}` : "";
  return `${apiBase}/mint-space/${mintSpaceId}/courses/${courseId}/cover${qs}`;
};

export const createChapter = async (mintSpaceId, courseId, payload = {}) => {
  const response = await api.post(`${base(mintSpaceId)}/${courseId}/chapters`, payload);
  return response.data;
};

export const updateChapter = async (mintSpaceId, courseId, chapterId, payload) => {
  const response = await api.patch(
    `${base(mintSpaceId)}/${courseId}/chapters/${chapterId}`,
    payload
  );
  return response.data;
};

export const deleteChapter = async (mintSpaceId, courseId, chapterId) => {
  const response = await api.delete(
    `${base(mintSpaceId)}/${courseId}/chapters/${chapterId}`
  );
  return response.data;
};

export const createLesson = async (mintSpaceId, courseId, chapterId, payload) => {
  const response = await api.post(
    `${base(mintSpaceId)}/${courseId}/chapters/${chapterId}/lessons`,
    payload
  );
  return response.data;
};

export const getLessonDetail = async (mintSpaceId, courseId, lessonId) => {
  const response = await api.get(
    `${base(mintSpaceId)}/${courseId}/lessons/${lessonId}`
  );
  return response.data;
};

export const updateLesson = async (mintSpaceId, courseId, lessonId, payload) => {
  const response = await api.patch(
    `${base(mintSpaceId)}/${courseId}/lessons/${lessonId}`,
    payload
  );
  return response.data;
};

export const deleteLesson = async (mintSpaceId, courseId, lessonId) => {
  const response = await api.delete(
    `${base(mintSpaceId)}/${courseId}/lessons/${lessonId}`
  );
  return response.data;
};

export const uploadLessonVideo = async (
  mintSpaceId,
  courseId,
  lessonId,
  { video, videoDuration }
) => {
  const formData = new FormData();
  formData.append("video", video);
  if (video?.name) formData.append("fileName", video.name);
  if (videoDuration) formData.append("videoDuration", String(videoDuration));
  const response = await api.post(
    `${base(mintSpaceId)}/${courseId}/lessons/${lessonId}/video`,
    formData,
    { timeout: 0 }
  );
  return response.data;
};

export const uploadLessonPdf = async (mintSpaceId, courseId, lessonId, file) => {
  const formData = new FormData();
  formData.append("pdf", file);
  if (file?.name) formData.append("fileName", file.name);
  const response = await api.post(
    `${base(mintSpaceId)}/${courseId}/lessons/${lessonId}/pdf`,
    formData,
    { timeout: 0 }
  );
  return response.data;
};

export const fetchLessonVideoBlob = async (mintSpaceId, courseId, lessonId) => {
  const response = await api.get(
    `${base(mintSpaceId)}/${courseId}/lessons/${lessonId}/stream`,
    { responseType: "blob" }
  );
  return response.data;
};

export const getLessonVideoStreamUrl = (mintSpaceId, courseId, lessonId) => {
  const apiBase = API_URL.replace(/\/$/, "");
  const token = localStorage.getItem("token");
  const qs = token ? `?access_token=${encodeURIComponent(token)}` : "";
  return `${apiBase}/mint-space/${mintSpaceId}/courses/${courseId}/lessons/${lessonId}/stream${qs}`;
};

export const getLessonPdfUrl = (mintSpaceId, courseId, lessonId) => {
  const apiBase = API_URL.replace(/\/$/, "");
  const token = localStorage.getItem("token");
  const qs = token ? `?access_token=${encodeURIComponent(token)}` : "";
  return `${apiBase}/mint-space/${mintSpaceId}/courses/${courseId}/lessons/${lessonId}/pdf${qs}`;
};

export const saveLessonProgress = async (mintSpaceId, courseId, lessonId, payload) => {
  const response = await api.put(
    `${base(mintSpaceId)}/${courseId}/lessons/${lessonId}/progress`,
    payload
  );
  return response.data;
};

export const uploadLessonAttachment = async (mintSpaceId, courseId, lessonId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  if (file?.name) formData.append("fileName", file.name);
  const response = await api.post(
    `${base(mintSpaceId)}/${courseId}/lessons/${lessonId}/attachments`,
    formData,
    { timeout: 0 }
  );
  return response.data;
};

export const deleteLessonAttachment = async (mintSpaceId, courseId, attachmentId) => {
  const response = await api.delete(
    `${base(mintSpaceId)}/${courseId}/attachments/${attachmentId}`
  );
  return response.data;
};

export const getLessonAttachmentDownloadUrl = (mintSpaceId, courseId, attachmentId) => {
  const apiBase = API_URL.replace(/\/$/, "");
  const token = localStorage.getItem("token");
  const qs = token ? `?access_token=${encodeURIComponent(token)}` : "";
  return `${apiBase}/mint-space/${mintSpaceId}/courses/${courseId}/attachments/${attachmentId}/download${qs}`;
};
